import { useState } from "react";
import styled, { keyframes } from "styled-components";
import { useSelector } from "react-redux";
import { selectUser } from "../../features/userSlice";
import { submitVerificationRequest } from "../../utils/verified";

export const RequestVerification = ({ onClose }) => {
    const user = useSelector(selectUser);
    const [category, setCategory] = useState("Individual");
    const [requestedBadge, setRequestedBadge] = useState("blue");
    const [reason, setReason] = useState("");
    const [documentUrl, setDocumentUrl] = useState("");
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!reason.trim()) {
            setError("Please describe why you should be verified.");
            return;
        }
        setLoading(true);
        setError("");
        try {
            await submitVerificationRequest(user, {
                category,
                requestedBadge,
                reason,
                documentUrl,
            });
            setSubmitted(true);
        } catch (err) {
            setError("Submission failed. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Overlay onClick={onClose}>
            <Modal onClick={(e) => e.stopPropagation()}>
                <CloseBtn onClick={onClose} aria-label="Close">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                    </svg>
                </CloseBtn>

                {submitted ? (
                    <SuccessState>
                        <div className="icon">🎉</div>
                        <h2>Request Submitted!</h2>
                        <p>
                            Your verification request is under review. You'll be notified
                            once an administrator processes it.
                        </p>
                        <button onClick={onClose}>Got it</button>
                    </SuccessState>
                ) : (
                    <>
                        <ModalHeader>
                            <ShieldBadge />
                            <div>
                                <h2>Request Verification</h2>
                                <p>Apply for a Fellora verification badge</p>
                            </div>
                        </ModalHeader>

                        <BadgeOptions>
                            <BadgeOption
                                $active={requestedBadge === "blue"}
                                $color="#1DA1F2"
                                onClick={() => setRequestedBadge("blue")}
                            >
                                <div className="badge-circle blue">✓</div>
                                <div>
                                    <strong>Blue Badge</strong>
                                    <span>For individuals — students, lecturers, creators</span>
                                </div>
                            </BadgeOption>
                            <BadgeOption
                                $active={requestedBadge === "gold"}
                                $color="#c9a063"
                                onClick={() => setRequestedBadge("gold")}
                            >
                                <div className="badge-circle gold">✓</div>
                                <div>
                                    <strong>Gold Badge</strong>
                                    <span>For organizations — universities, associations, NGOs</span>
                                </div>
                            </BadgeOption>
                        </BadgeOptions>

                        <form onSubmit={handleSubmit}>
                            <Field>
                                <label>Category</label>
                                <select value={category} onChange={(e) => setCategory(e.target.value)}>
                                    <option>Individual</option>
                                    <option>Student</option>
                                    <option>Lecturer / Researcher</option>
                                    <option>University Staff</option>
                                    <option>Public Figure</option>
                                    <option>Content Creator</option>
                                    <option>University</option>
                                    <option>Student Association</option>
                                    <option>Company</option>
                                    <option>NGO / Official Club</option>
                                    <option>Government Institution</option>
                                </select>
                            </Field>

                            <Field>
                                <label>Why should you be verified?</label>
                                <textarea
                                    rows={4}
                                    placeholder="Describe your role, achievements, or why this account represents a real person or organization…"
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    required
                                />
                            </Field>

                            <Field>
                                <label>Supporting document URL <span>(optional)</span></label>
                                <input
                                    type="url"
                                    placeholder="https://your-university.edu/staff/yourname"
                                    value={documentUrl}
                                    onChange={(e) => setDocumentUrl(e.target.value)}
                                />
                            </Field>

                            {error && <ErrorMsg>{error}</ErrorMsg>}

                            <SubmitBtn type="submit" disabled={loading}>
                                {loading ? "Submitting…" : "Submit Request"}
                            </SubmitBtn>
                        </form>
                    </>
                )}
            </Modal>
        </Overlay>
    );
};

const ShieldBadge = () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="#1DA1F2">
        <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 13l-3-3 1.4-1.4L10 11.2l5.6-5.6L17 7l-7 7z" />
    </svg>
);

// ─── Animations ───────────────────────────────────────────────────────────────
const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const slideUp = keyframes`
    from { transform: translateY(30px); opacity: 0; }
    to   { transform: translateY(0);    opacity: 1; }
`;

// ─── Styled Components ────────────────────────────────────────────────────────
const Overlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(10, 20, 40, 0.55);
    z-index: 10000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    animation: ${fadeIn} 0.2s ease;
`;

const Modal = styled.div`
    background: #ffffff;
    border-radius: 18px;
    width: 100%;
    max-width: 520px;
    padding: 28px 28px 24px;
    position: relative;
    animation: ${slideUp} 0.25s ease;
    box-shadow: 0 20px 60px rgba(10, 20, 40, 0.18);
`;

const CloseBtn = styled.button`
    position: absolute;
    top: 16px;
    right: 16px;
    background: #f5f5f5;
    border: none;
    border-radius: 50%;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #555;
    transition: background 0.15s;

    &:hover { background: #e0e0e0; color: #000; }
`;

const ModalHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 20px;

    h2 {
        font-size: 24px;
        font-weight: 700;
        color: #0a1428;
        margin: 0;
    }
    p {
        font-size: 16px;
        color: #888;
        margin: 2px 0 0;
    }
`;

const BadgeOptions = styled.div`
    display: flex;
    gap: 10px;
    margin-bottom: 20px;

    @media (max-width: 480px) { flex-direction: column; }
`;

const BadgeOption = styled.div`
    flex: 1;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 14px;
    border-radius: 12px;
    border: 2px solid ${(p) => (p.$active ? p.$color : "rgba(201,160,99,0.3)")};
    background: ${(p) => (p.$active ? `${p.$color}12` : "#faf7f2")};
    cursor: pointer;
    transition: all 0.18s;

    &:hover { border-color: ${(p) => p.$color}; }

    .badge-circle {
        width: 28px;
        height: 28px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
        font-weight: 700;
        color: white;
        flex-shrink: 0;

        &.blue { background: #1DA1F2; }
        &.gold { background: linear-gradient(135deg, #c9a063, #e8c47d); }
    }

    div {
        display: flex;
        flex-direction: column;
        gap: 1px;

        strong { font-size: 18px; font-weight: 700; color: #0a1428; }
        span   { font-size: 14px; color: #888; line-height: 1.3; }
    }
`;

const Field = styled.div`
    margin-bottom: 14px;

    label {
        display: block;
        font-size: 18px;
        font-weight: 700;
        color: #0a1428;
        margin-bottom: 5px;

        span { font-weight: 400; color: #999; font-size: 15px; }
    }

    input, select, textarea {
        width: 100%;
        padding: 10px 14px;
        border: 1.5px solid rgba(201, 160, 99, 0.4);
        border-radius: 8px;
        font-size: 18px;
        font-family: inherit;
        outline: none;
        transition: border-color 0.2s;
        background: #faf7f2;
        box-sizing: border-box;

        &:focus { border-color: #1DA1F2; background: #fff; }
    }

    textarea { resize: vertical; }
`;

const ErrorMsg = styled.div`
    font-size: 17px;
    color: #e53935;
    margin-bottom: 10px;
    font-weight: 600;
`;

const SubmitBtn = styled.button`
    width: 100%;
    padding: 13px 0;
    background: linear-gradient(135deg, #1DA1F2, #0d8ecf);
    color: white;
    border: none;
    border-radius: 30px;
    font-size: 20px;
    font-weight: 700;
    cursor: pointer;
    transition: opacity 0.2s;

    &:hover { opacity: 0.88; }
    &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const SuccessState = styled.div`
    text-align: center;
    padding: 20px 0;

    .icon { font-size: 52px; margin-bottom: 14px; }
    h2 { font-size: 26px; font-weight: 700; color: #0a1428; margin-bottom: 10px; }
    p { font-size: 19px; color: #666; line-height: 1.5; margin-bottom: 24px; }

    button {
        padding: 11px 36px;
        background: #0a1428;
        color: white;
        border: none;
        border-radius: 30px;
        font-size: 19px;
        font-weight: 700;
        cursor: pointer;
        transition: opacity 0.2s;

        &:hover { opacity: 0.85; }
    }
`;

export default RequestVerification;
